// src/useForm.ts
import { useState, ChangeEvent, useCallback, useMemo } from 'react';
import { ZodError } from 'zod';
import { UseFormProps, UseFormResult } from './types';

export function useForm<TData extends object>({
  initialValues,
  schema,
  validateOnChange = false,
  validateOnBlur = true,
  onSubmit,
}: UseFormProps<TData>): UseFormResult<TData> {
  const [values, setValuesState] = useState<TData>(initialValues);
  const [errors, setErrors] = useState<Record<keyof TData, string | undefined>>(
    {} as Record<keyof TData, string | undefined>
  );
  const [touched, setTouched] = useState<Record<keyof TData, boolean>>(
    {} as Record<keyof TData, boolean>
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if form is dirty (has been modified)
  const isDirty = useMemo(() => {
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  // Check if form is valid
  const isValid = useMemo(() => {
    try {
      schema.parse(values);
      return true;
    } catch {
      return false;
    }
  }, [values, schema]);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const inputValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    
    setValuesState((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
    
    // Clear error for the field being edited
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Validate on change if enabled
    if (validateOnChange) {
      setTimeout(() => validateField(name as keyof TData), 0);
    }
  }, [validateOnChange]);

  const handleBlur = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = event.target;
    
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate on blur if enabled
    if (validateOnBlur) {
      validateField(name as keyof TData);
    }
  }, [validateOnBlur, values]);

  const validate = useCallback((data: TData) => {
    try {
      schema.parse(data);
      setErrors({} as Record<keyof TData, string | undefined>);
      return true;
    } catch (err) {
      if (err instanceof ZodError) {
        const fieldErrors = {} as Record<keyof TData, string | undefined>;
        err.issues.forEach((issue) => {
          const key = issue.path[0] as keyof TData;
          fieldErrors[key] = issue.message;
        });
        setErrors(fieldErrors);
        return false;
      }
      return false;
    }
  }, [schema]);

  const validateField = useCallback(<K extends keyof TData>(field: K): boolean => {
    try {
      // Validate the entire object but only set error for this field
      schema.parse(values);
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
      return true;
    } catch (err) {
      if (err instanceof ZodError) {
        const fieldError = err.issues.find(issue => issue.path[0] === field);
        setErrors((prev) => ({
          ...prev,
          [field]: fieldError?.message,
        }));
        return !fieldError;
      }
      return false;
    }
  }, [values, schema]);

  const handleSubmit = useCallback((callback?: (data: TData) => void | Promise<void>) => async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key as keyof TData] = true;
      return acc;
    }, {} as Record<keyof TData, boolean>);
    setTouched(allTouched);

    if (validate(values)) {
      setIsSubmitting(true);
      try {
        const submitCallback = callback || onSubmit;
        if (submitCallback) {
          await submitCallback(values);
        }
      } catch (error) {
        console.error('Form submission error:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [values, validate, onSubmit]);

  const reset = useCallback((newValues?: TData) => {
    setValuesState(newValues || initialValues);
    setErrors({} as Record<keyof TData, string | undefined>);
    setTouched({} as Record<keyof TData, boolean>);
    setIsSubmitting(false);
  }, [initialValues]);

  const setFieldValue = useCallback(<K extends keyof TData>(field: K, value: TData[K]) => {
    setValuesState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const setFieldError = useCallback(<K extends keyof TData>(field: K, error: string | undefined) => {
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  const setFieldTouched = useCallback(<K extends keyof TData>(field: K, isTouched: boolean) => {
    setTouched((prev) => ({
      ...prev,
      [field]: isTouched,
    }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({} as Record<keyof TData, string | undefined>);
  }, []);

  const setValues = useCallback((newValues: Partial<TData>) => {
    setValuesState((prev) => ({
      ...prev,
      ...newValues,
    }));
  }, []);

  const getFieldProps = useCallback(<K extends keyof TData>(field: K) => {
    return {
      name: field as string,
      value: values[field],
      onChange: handleChange,
      onBlur: handleBlur,
    };
  }, [values, handleChange, handleBlur]);

  return {
    values,
    errors,
    touched,
    isDirty,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    validateField,
    reset,
    clearErrors,
    setValues,
    getFieldProps,
  };
}